import { ContractData } from "@/pages/contracts/types";

export interface MetricSources {
  /** Manual override persisted on the contract row. Editable. */
  recognizedLocal: number | null;
  recognizedAccount: number | null;
  /** Derived from the contract's service items. Read-only. */
  calculatedLocal: number | null;
  calculatedAccount: number | null;
}

export interface EffectiveMetric {
  /** Contract-currency amount to display. */
  local: number | null;
  /** Account-currency amount for the parenthesised conversion. */
  account: number | null;
}

// An override wins over the calculated figure, including an explicit 0 — that is a
// real frozen value, hence `!= null` rather than truthiness. The account amount
// follows whichever source won, so the conversion never describes a different
// figure than the one displayed; a winning source with no account amount yields
// null and the caller shows no conversion. Named fields, not positional args:
// all four are `number | null` and a mis-order would type-check silently.
export const resolveEffectiveMetric = ({
  recognizedLocal,
  recognizedAccount,
  calculatedLocal,
  calculatedAccount
}: MetricSources): EffectiveMetric =>
  recognizedLocal != null
    ? { local: recognizedLocal, account: recognizedAccount }
    : { local: calculatedLocal, account: calculatedAccount };

export interface CommittedRevenue extends MetricSources {
  effective: EffectiveMetric;
}

type CommittedRevenueFields = Pick<
  ContractData,
  | "carr"
  | "carrLocal"
  | "cmrr"
  | "cmrrLocal"
  | "calculatedCarr"
  | "calculatedCarrLocal"
  | "calculatedCmrr"
  | "calculatedCmrrLocal"
>;

// Single source of truth for the committed-revenue metric (CARR in ARR mode, CMRR
// in MRR mode). Every surface rendering one value for it resolves through here, so
// they cannot drift apart the way the contract header did.
export const getCommittedRevenue = (
  contract: CommittedRevenueFields,
  isArr: boolean
): CommittedRevenue => {
  const sources: MetricSources = {
    recognizedLocal: isArr ? contract.carrLocal : contract.cmrrLocal,
    recognizedAccount: isArr ? contract.carr : contract.cmrr,
    calculatedLocal: isArr
      ? contract.calculatedCarrLocal
      : contract.calculatedCmrrLocal,
    calculatedAccount: isArr ? contract.calculatedCarr : contract.calculatedCmrr
  };

  return { ...sources, effective: resolveEffectiveMetric(sources) };
};
